import {useCallback} from "react";
import {SoundFile, Spot, Zone} from "../../components/types";
import {AppContextI} from "../../components/context/ContextWrapper";
import {UserContextI} from "../../UserContext";
import {SoundLibraryDragPayload} from "../../components/soundfiles/SoundFileDrawer";
import {applyCueTriggerSetToSpot, CueTriggerSet, hasCueTriggers} from "./cueTriggerHelpers";
import {createCueSpot, getNextCueIndex} from "./cueListHelpers";
import {getCueLabelFromFileName} from "./cueListRuleBuilders";

type UploadedFile = {
    originalFileName: string;
    fileName: string;
    filePath: string;
    metadata: {
        mimeType?: string;
        sizeBytes?: number;
        durationMs?: number;
    };
};

type Deps = {
    appContext: AppContextI;
    userContext: UserContextI;
    area: {id?: string; zones?: any} | undefined;
    canEdit: boolean;
    orderedCues: Spot[];
    defaultTriggerSet: CueTriggerSet;
    handleUpdate: (spot: Spot, updates: Partial<Spot>) => void;
    handleUpdateAmbientZone: (zone: Zone, updates: Partial<Zone>) => void;
    registerSoundFile: (options: {variables: any}) => Promise<any>;
    enqueueUploads: (
        files: File[],
        onUploaded: (uploaded: UploadedFile) => Promise<void>,
        onFailed?: (reason: "cancelled" | "error", message?: string) => void
    ) => void;
    buildOptimisticCreatedCues: (existingCues: Spot[], createdCues: Spot[]) => Spot[];
    mergeSpotsIntoContext: (spots: Spot[]) => void;
    applyFullChainByIds: (spotIds: string[], fallbackSpots?: Spot[]) => void;
    markCueUploading?: (spotId: string, fileName: string) => void;
    clearCueUploading?: (spotId: string) => void;
};

export const useCueListSoundAssignment = (deps: Deps) => {
    const {
        appContext, userContext, area, canEdit, orderedCues, defaultTriggerSet,
        handleUpdate, handleUpdateAmbientZone, registerSoundFile, enqueueUploads,
        buildOptimisticCreatedCues, mergeSpotsIntoContext, applyFullChainByIds,
        markCueUploading, clearCueUploading
    } = deps;

    const assignUploadedFileToSpot = useCallback(async (spot: Spot, uploaded: UploadedFile) => {
        const liveSpot = appContext.getSpot(spot.id) || spot;
        const liveSound = liveSpot.soundFiles?.[0];
        const nextSound: SoundFile = {
            ...liveSound,
            synchronize: liveSound?.synchronize ?? false,
            originalFileName: uploaded.originalFileName,
            fileName: uploaded.fileName,
            filePath: uploaded.filePath,
            delay: liveSound?.delay ?? 0,
            fadeIn: liveSound?.fadeIn ?? 0,
            fadeOut: liveSound?.fadeOut ?? 0,
            volumeDb: liveSound?.volumeDb ?? 0
        };
        handleUpdate(liveSpot, {soundFiles: [nextSound]});
        const areaId = area?.id;
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        if (areaId) {
            try {
                await registerSoundFile({
                    variables: {
                        areaId,
                        objectId: uploaded.fileName,
                        originalFileName: uploaded.originalFileName,
                        filePath: uploaded.filePath,
                        uploadedBy,
                        mimeType: uploaded.metadata.mimeType,
                        sizeBytes: uploaded.metadata.sizeBytes,
                        durationMs: uploaded.metadata.durationMs
                    }
                });
            } catch (error) {
                console.warn("Unable to register sound file", error);
            }
        }
    }, [appContext, area?.id, handleUpdate, registerSoundFile, userContext]);

    const assignUploadedFileToAmbientZone = useCallback(async (zone: Zone, uploaded: UploadedFile) => {
        const latestArea = appContext.getArea() || area;
        const latestZone = latestArea?.zones?.ambientZones?.find((item: Zone) => item.id === zone.id) || zone;
        const currentSound = latestZone.soundFiles?.ambient;
        const nextAmbientSound: SoundFile = {
            ...currentSound,
            synchronize: currentSound?.synchronize ?? false,
            originalFileName: uploaded.originalFileName,
            fileName: uploaded.fileName,
            filePath: uploaded.filePath,
            delay: currentSound?.delay ?? 0,
            fadeIn: currentSound?.fadeIn ?? 0,
            fadeOut: currentSound?.fadeOut ?? 0,
            volumeDb: currentSound?.volumeDb ?? 0
        };
        handleUpdateAmbientZone(latestZone, {
            soundFiles: {
                ...latestZone.soundFiles,
                ambient: nextAmbientSound
            }
        });
        const areaId = latestArea?.id;
        const uploadedBy = userContext.getUser()?.email || userContext.getUser()?.fullName;
        if (areaId) {
            try {
                await registerSoundFile({
                    variables: {
                        areaId,
                        objectId: uploaded.fileName,
                        originalFileName: uploaded.originalFileName,
                        filePath: uploaded.filePath,
                        uploadedBy,
                        mimeType: uploaded.metadata.mimeType,
                        sizeBytes: uploaded.metadata.sizeBytes,
                        durationMs: uploaded.metadata.durationMs
                    }
                });
            } catch (error) {
                console.warn("Unable to register ambient sound file", error);
            }
        }
    }, [appContext, area, handleUpdateAmbientZone, registerSoundFile, userContext]);

    const assignExistingSoundToSpot = useCallback((spot: Spot, selectedSound: SoundLibraryDragPayload) => {
        const liveSpot = appContext.getSpot(spot.id) || spot;
        const liveSound = liveSpot.soundFiles?.[0];
        if (!selectedSound.fileName) {
            return;
        }
        const nextSound: SoundFile = {
            ...liveSound,
            synchronize: liveSound?.synchronize ?? false,
            originalFileName: selectedSound.originalFileName || selectedSound.fileName,
            fileName: selectedSound.fileName,
            filePath: selectedSound.filePath || "SoundFiles",
            delay: liveSound?.delay ?? 0,
            fadeIn: liveSound?.fadeIn ?? 0,
            fadeOut: liveSound?.fadeOut ?? 0,
            volumeDb: liveSound?.volumeDb ?? 0
        };
        handleUpdate(liveSpot, {soundFiles: [nextSound]});
    }, [appContext, handleUpdate]);

    const assignExistingSoundToAmbientZone = useCallback((zone: Zone, selectedSound: SoundLibraryDragPayload) => {
        const latestArea = appContext.getArea() || area;
        const latestZone = latestArea?.zones?.ambientZones?.find((item: Zone) => item.id === zone.id) || zone;
        const currentSound = latestZone.soundFiles?.ambient;
        if (!selectedSound.fileName) {
            return;
        }
        const nextAmbientSound: SoundFile = {
            ...currentSound,
            synchronize: currentSound?.synchronize ?? false,
            originalFileName: selectedSound.originalFileName || selectedSound.fileName,
            fileName: selectedSound.fileName,
            filePath: selectedSound.filePath || "SoundFiles",
            delay: currentSound?.delay ?? 0,
            fadeIn: currentSound?.fadeIn ?? 0,
            fadeOut: currentSound?.fadeOut ?? 0,
            volumeDb: currentSound?.volumeDb ?? 0
        };
        handleUpdateAmbientZone(latestZone, {
            soundFiles: {
                ...latestZone.soundFiles,
                ambient: nextAmbientSound
            }
        });
    }, [appContext, area, handleUpdateAmbientZone]);

    const createCuesFromDroppedFiles = useCallback(async (droppedFiles: File[]) => {
        if (!canEdit || !area?.id || droppedFiles.length === 0) {
            return;
        }

        const createdPairs: Array<{file: File; spot: Spot}> = [];
        const startIndex = getNextCueIndex(orderedCues);

        for (let i = 0; i < droppedFiles.length; i++) {
            const file = droppedFiles[i];
            const cueLabel = getCueLabelFromFileName(file.name);
            const cueSpot = createCueSpot(area.id, cueLabel, startIndex + i);

            const createdSpot = await appContext.createSpot(cueSpot as Spot, {select: false, suppressLocalInsert: true});
            if (createdSpot) {
                const createdSpotWithDefaults = hasCueTriggers(defaultTriggerSet)
                    ? {...createdSpot, ...applyCueTriggerSetToSpot(defaultTriggerSet)}
                    : createdSpot;
                createdPairs.push({file, spot: createdSpotWithDefaults});
            }
        }

        if (createdPairs.length === 0) {
            return;
        }

        const optimisticCreated = buildOptimisticCreatedCues(orderedCues, createdPairs.map((pair) => pair.spot));
        mergeSpotsIntoContext(optimisticCreated);
        const intendedCues = [...orderedCues, ...optimisticCreated];
        const intendedCueIds = intendedCues.map((spot) => spot.id);
        applyFullChainByIds(intendedCueIds, intendedCues);
        createdPairs.forEach(({file, spot}, index) => {
            const optimisticSpot = optimisticCreated[index] || spot;
            markCueUploading?.(optimisticSpot.id, file.name);
            enqueueUploads(
                [file],
                async (uploaded) => {
                    try {
                        await assignUploadedFileToSpot(optimisticSpot, uploaded);
                        applyFullChainByIds(intendedCueIds);
                    } finally {
                        clearCueUploading?.(optimisticSpot.id);
                    }
                },
                () => {
                    // Upload cancelled or failed — drop the empty cue we created
                    clearCueUploading?.(optimisticSpot.id);
                    const liveSpot = appContext.getSpot(optimisticSpot.id);
                    if (liveSpot && !(liveSpot.soundFiles?.length)) {
                        appContext.deleteSelectedSpot(liveSpot);
                    }
                }
            );
        });
    }, [appContext, applyFullChainByIds, area?.id, assignUploadedFileToSpot, canEdit, enqueueUploads, orderedCues, defaultTriggerSet, buildOptimisticCreatedCues, mergeSpotsIntoContext, markCueUploading, clearCueUploading]);

    const createCueFromExistingSound = useCallback(async (selectedSound: SoundLibraryDragPayload) => {
        if (!canEdit || !area?.id || !selectedSound.fileName) {
            return;
        }
        const nextIndex = getNextCueIndex(orderedCues);
        const cueLabel = getCueLabelFromFileName(selectedSound.originalFileName || selectedSound.fileName);
        const cueSpot = createCueSpot(area.id, cueLabel, nextIndex);
        const createdSpot = await appContext.createSpot(cueSpot as Spot, {select: false, suppressLocalInsert: true});
        if (!createdSpot) {
            return;
        }
        const createdSpotWithDefaults = hasCueTriggers(defaultTriggerSet)
            ? {...createdSpot, ...applyCueTriggerSetToSpot(defaultTriggerSet)}
            : createdSpot;
        const optimisticCreated = buildOptimisticCreatedCues(orderedCues, [createdSpotWithDefaults]);
        mergeSpotsIntoContext(optimisticCreated);
        const targetSpot = optimisticCreated[0] || createdSpotWithDefaults;
        const intendedCues = [...orderedCues, targetSpot];
        const intendedCueIds = intendedCues.map((spotItem) => spotItem.id);
        applyFullChainByIds(intendedCueIds, intendedCues);
        assignExistingSoundToSpot(targetSpot, selectedSound);
        applyFullChainByIds(intendedCueIds);
    }, [appContext, applyFullChainByIds, area?.id, assignExistingSoundToSpot, canEdit, orderedCues, defaultTriggerSet, buildOptimisticCreatedCues, mergeSpotsIntoContext]);

    return {
        assignUploadedFileToSpot,
        assignUploadedFileToAmbientZone,
        assignExistingSoundToSpot,
        assignExistingSoundToAmbientZone,
        createCuesFromDroppedFiles,
        createCueFromExistingSound
    };
};
